import * as React from "react";

import { useDatasetsQuery } from "@/shared/domain/datasets/hooks";
import type { SearchQueryBody } from "@/types";

/**
 * Human-readable names for a handful of datasets, by id.
 *
 * A flat file list has no dataset headings to sit under, so each row has to
 * name its own dataset. Ids are always available on the file record and names
 * are not, which decides the contract: the returned function **always** answers,
 * with the name once it is known and the id until then.
 *
 * That fallback is the point rather than a nicety. The alternatives are both
 * lies — rendering nothing claims the file belongs nowhere, and rendering
 * "Unknown dataset" states as fact something that is merely not fetched yet.
 * An id is unlovely but true, and it is still enough to tell two same-named
 * files apart, which is what the label is for.
 *
 * Scoped to the ids actually on screen rather than listing the org's datasets:
 * a picker showing ten files should not pull a thousand dataset records, and
 * an org with more datasets than one page holds would otherwise leave the
 * overflow permanently unnamed.
 *
 * @param orgId the org the datasets belong to
 * @param datasetIds the ids needing a label; an empty list issues no request
 * @returns a stable lookup from dataset id to its display label
 */
export function useDatasetLabels(
  orgId: string,
  datasetIds: string[],
): (datasetId: string) => string {
  // Sorted and de-duplicated so the query key depends on the *set* of ids, not
  // the order the file listing happened to return them in — otherwise a re-sort
  // of the same datasets refetches every name.
  const ids = React.useMemo(
    () => Array.from(new Set(datasetIds)).sort(),
    [datasetIds],
  );

  const query = React.useMemo<SearchQueryBody>(
    () => ({
      condition: {
        operator: "OR",
        conditions: ids.map((id) => ({
          field: "dataset_id",
          comparator: "EQUALS" as const,
          value: id,
        })),
      },
    }),
    [ids],
  );

  const datasetsQuery = useDatasetsQuery({
    // Withholding the org is how this hook stays disabled: an OR over zero
    // conditions is not a query the backend accepts, and asking for the names
    // of no datasets has no answer worth waiting for.
    orgId: ids.length === 0 ? undefined : orgId,
    query,
  });

  const names = React.useMemo(() => {
    const byId = new Map<string, string>();
    for (const dataset of datasetsQuery.data ?? []) {
      // A dataset's name is nullable, and an all-whitespace one is no more
      // recognisable than none at all — both fall through to the id.
      const name = dataset.name?.trim();
      if (name) {
        byId.set(dataset.dataset_id, name);
      }
    }
    return byId;
  }, [datasetsQuery.data]);

  return React.useCallback(
    (datasetId: string) => names.get(datasetId) ?? datasetId,
    [names],
  );
}
